import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { InvoiceService } from '../../../../../Services/Invoice/invoice.service';
import { NotificationService } from '../../../../../Services/notification.service';
import { FirmSelectionService } from '../../../../../Services/firm-selection.service';
import { FormsModule } from '@angular/forms';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-udhar-list',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule],
  templateUrl: './udhar-list.component.html',
  styleUrl: './udhar-list.component.css'
})
export class UdharListComponent implements OnInit {

  // Summary Stats
  totalOutstandingCash: number = 0;
  totalOutstandingMetal: number = 0;
  criticalCount: number = 0;
  isLoading: boolean = false;

  // Data Lists
  debtors: any[] = [];
  filteredDebtors: any[] = [];
  paginatedDebtors: any[] = [];

  // Pagination
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 1;

  // Filters
  filterName: string = '';
  fromDate: string = '';
  toDate: string = '';

  // Sorting
  sortKey: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Range Filters
  minCash: number | null = null;
  maxCash: number | null = null;
  minMetal: number | null = null;
  maxMetal: number | null = null;

  constructor(
    private invoiceService: InvoiceService,
    private notificationService: NotificationService,
    private firmSelectionService: FirmSelectionService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.fetchUdharList();
  }

  payUdhar(customerId: number) {
    this.router.navigate(['/customer-dashboard', customerId]);
  }

  fetchUdharList() {
    this.isLoading = true;
    this.invoiceService.getAllCustomersUdhar().subscribe({
      next: (res: any) => {
        this.debtors = res.data.customers;
        this.totalOutstandingCash = res.data.total_outstanding_cash;
        this.totalOutstandingMetal = res.data.total_outstanding_metal;
        this.criticalCount = res.data.critical_count;
        this.applyFilters();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching udhar list:', err);
        this.notificationService.showError('Failed to load udhar list', 'Error');
        this.isLoading = false;
      }
    });
  }

  applyFilters() {
    this.filteredDebtors = this.debtors.filter(debtor => {
      const matchesName = debtor.name.toLowerCase().includes(this.filterName.toLowerCase());

      let matchesDate = true;
      if (this.fromDate && debtor.last_date) {
        matchesDate = matchesDate && (debtor.last_date >= this.fromDate);
      }
      if (this.toDate && debtor.last_date) {
        matchesDate = matchesDate && (debtor.last_date <= this.toDate);
      }

      // Cash Range
      let matchesCash = true;
      if (this.minCash !== null) matchesCash = matchesCash && (debtor.cash_due >= this.minCash);
      if (this.maxCash !== null) matchesCash = matchesCash && (debtor.cash_due <= this.maxCash);

      // Metal Range
      let matchesMetal = true;
      if (this.minMetal !== null) matchesMetal = matchesMetal && (debtor.metal_due >= this.minMetal);
      if (this.maxMetal !== null) matchesMetal = matchesMetal && (debtor.metal_due <= this.maxMetal);

      return matchesName && matchesDate && matchesCash && matchesMetal;
    });

    // Apply Sorting
    if (this.sortKey) {
      this.filteredDebtors.sort((a, b) => {
        const valA = a[this.sortKey];
        const valB = b[this.sortKey];

        if (typeof valA === 'string' && typeof valB === 'string') {
          return this.sortDirection === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }

        return this.sortDirection === 'asc' ? (valA - valB) : (valB - valA);
      });
    }

    this.currentPage = 1;
    this.updatePagination();
  }

  resetFilters() {
    this.filterName = '';
    this.fromDate = '';
    this.toDate = '';
    this.minCash = null;
    this.maxCash = null;
    this.minMetal = null;
    this.maxMetal = null;
    this.applyFilters();
  }

  toggleSort(key: string) {
    if (this.sortKey === key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDirection = 'desc'; // Default to desc for amounts (high to low)
    }
    this.applyFilters();
  }

  updatePagination() {
    this.totalPages = Math.ceil(this.filteredDebtors.length / this.itemsPerPage) || 1;
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    this.paginatedDebtors = this.filteredDebtors.slice(start, end);
  }

  goToPage(page: number | string) {
    if (typeof page === 'string') return;
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  get pageNumbers(): (number | string)[] {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    const maxPagesToShow = 5;
    const pages: (number | string)[] = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) {
        pages.push('...');
      }
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 2) {
        startPage = 2;
        endPage = 3;
      }
      if (currentPage >= totalPages - 1) {
        startPage = totalPages - 2;
        endPage = totalPages - 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      pages.push(totalPages);
    }
    return pages;
  }

  exportToExcel() {
    const firmName = this.firmSelectionService.getselectedFirmName()?.name || 'My Firm';
    const fileName = `Outstanding_Payments_${firmName.replace(/\s+/g, '_')}.xlsx`;

    const dataToExport = this.filteredDebtors.map((item, index) => ({
      'SR. NO.': index + 1,
      'CLIENT NAME': item.name,
      'MOBILE': item.mobile,
      'LOCATION': item.location,
      'LAST TRANSACTION': item.last_date || 'N/A',
      'METAL DUE (GM)': Number(item.metal_due),
      'CASH DUE (Rs.)': Number(item.cash_due)
    }));

    const worksheet = XLSX.utils.json_to_sheet([]);
    XLSX.utils.sheet_add_aoa(worksheet, [
      [`${firmName.toUpperCase()} - OUTSTANDING PAYMENTS REPORT`],
      [`GENERATED ON: ${new Date().toLocaleString('en-GB')}`],
      [`TOTAL OUTSTANDING: ₹ ${this.totalOutstandingCash.toLocaleString('en-IN')}`],
      []
    ], { origin: "A1" });

    XLSX.utils.sheet_add_json(worksheet, dataToExport, { origin: "A5", skipHeader: false });

    // Column widths
    worksheet['!cols'] = [
      { wch: 10 }, { wch: 30 }, { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 15 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Outstanding');
    XLSX.writeFile(workbook, fileName);
    this.notificationService.showSuccess('Excel exported successfully', 'Success');
  }

  exportToPDF() {
    const firmName = this.firmSelectionService.getselectedFirmName()?.name || 'My Firm';
    const fileName = `Outstanding_Payments_${firmName.replace(/\s+/g, '_')}.pdf`;

    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape

    doc.setFontSize(20);
    doc.setTextColor(12, 68, 125); // #0C447D
    doc.text('OUTSTANDING PAYMENTS REPORT', 148, 20, { align: 'center' });

    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(firmName, 148, 28, { align: 'center' });

    const headers = [['SR.', 'CLIENT NAME', 'MOBILE', 'LOCATION', 'LAST TRANS.', 'METAL DUE (GM)', 'CASH DUE (Rs.)']];
    const data = this.filteredDebtors.map((item, index) => [
      index + 1,
      item.name,
      item.mobile,
      item.location,
      item.last_date || 'N/A',
      item.metal_due.toFixed(3),
      item.cash_due.toLocaleString('en-IN', { minimumFractionDigits: 2 })
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 35,
      theme: 'grid',
      headStyles: { fillColor: [12, 68, 125], textColor: 255 },
      styles: { fontSize: 9 }
    });

    doc.save(fileName);
    this.notificationService.showSuccess('PDF exported successfully', 'Success');
  }

  sendReminder(name: string) {
    this.notificationService.showInfo(`Reminder feature for ${name} coming soon!`, 'Note');
  }
}